import { useEffect, useRef } from 'react'
import { Delete } from 'lucide-react'
import clsx from 'clsx'

interface Props {
  valor: string
  onCambio: (valor: string) => void

  /** Se llama al completar el largo, con el PIN entero. */
  onConfirmar?: (valor: string) => void

  largo?: number
  deshabilitado?: boolean

  /**
   * Variante para fondo oscuro. La entrada de la caja va sobre el color de la
   * marca, y las teclas claras sobre ese fondo encandilan en una pantalla de
   * mostrador a las siete de la mañana.
   */
  oscuro?: boolean
}

// Teclado numérico para el PIN de la caja.
//
// Existe porque en el mostrador el login se hace con la pantalla táctil y las
// manos ocupadas: un input de texto con el teclado del sistema tapando media
// pantalla no sirve. Las teclas son de 64px porque menos que eso no se acierta
// con los dedos mojados.
//
// Y ADEMÁS se puede tipear, que es la otra mitad y faltaba: en la oficina, en
// la tablet con teclado y en cualquier PC con numpad, obligar a apuntarle con
// el mouse a cuatro botones es más lento que el gesto que la persona ya hace
// sin pensar. Es la misma pantalla para los dos usos y no hay que elegir.
export function PinPad({
  valor,
  onCambio,
  onConfirmar,
  largo = 4,
  deshabilitado,
  oscuro,
}: Props) {
  const agregar = (d: string) => {
    if (deshabilitado || valor.length >= largo) return
    const nuevo = valor + d
    onCambio(nuevo)

    // Se confirma solo al completar el largo: un dígito menos de fricción por
    // login, y son cientos de logins por semana.
    // Se manda el PIN completo como argumento y no se deja que el llamador lo
    // lea del estado: el estado de React todavía no se actualizó cuando esto
    // corre, así que leerlo daría el PIN con un dígito de menos.
    if (nuevo.length === largo) onConfirmar?.(nuevo)
  }

  const borrar = () => {
    if (!deshabilitado) onCambio(valor.slice(0, -1))
  }

  // El listener va en `window` y no en un input, porque acá no hay input: los
  // únicos nodos enfocables son las nueve teclas, así que con el foco en el
  // cuerpo un handler colgado del componente no vería nada. Es el mismo
  // criterio que la tecla rápida del medio de pago.
  //
  // Y por eso mismo entra por una REF y no por las dependencias del efecto:
  // los llamadores pasan lambdas nuevas en cada render, así que suscribirse de
  // nuevo cada vez sería reenganchar el listener con cada dígito.
  const raiz = useRef<HTMLDivElement>(null)

  const alTeclado = (e: KeyboardEvent) => {
    if (deshabilitado) return

    // Un atajo del sistema no es un dígito del PIN.
    if (e.ctrlKey || e.altKey || e.metaKey) return

    // Si alguien está TIPEANDO en un campo, la tecla es de ese campo. En estas
    // tres pantallas casi no hay otros campos, pero el pad del diálogo de
    // autorización aparece encima de cualquier formulario del sistema y ese sí
    // los tiene.
    const foco = document.activeElement
    if (foco instanceof HTMLInputElement || foco instanceof HTMLTextAreaElement) return

    // Con un diálogo abierto la tecla es del que está ARRIBA. Sin esto, el pad
    // de la entrada de la caja seguiría escuchando por debajo del diálogo de
    // autorización y los dos se comerían el mismo dígito. Se pregunta por
    // `dialog[open]` y no por una lista de diálogos, por lo mismo que el atajo
    // de Escape: una lista escrita a mano se queda corta con el próximo.
    const arriba = document.querySelector('dialog[open]')
    if (arriba && !arriba.contains(raiz.current)) return

    // Los dígitos valen de la fila de arriba y del numpad: `key` devuelve el
    // caracter en los dos casos.
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault()
      agregar(e.key)
      return
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      // El preventDefault no es decoración: en algunos navegadores un
      // Backspace sin foco en un campo es «volver atrás», y volver atrás desde
      // la entrada de la caja saca al cajero de la pantalla.
      e.preventDefault()
      borrar()
    }
  }

  // La ref se actualiza DESPUÉS de renderizar y no durante: escribirla en el
  // cuerpo del componente es lo que el linter prohíbe, y con razón. Corre en
  // cada render, así que el listener siempre llama a la versión que ve el
  // `valor` de ahora.
  const teclado = useRef(alTeclado)
  useEffect(() => {
    teclado.current = alTeclado
  })

  useEffect(() => {
    const escuchar = (e: KeyboardEvent) => teclado.current(e)
    window.addEventListener('keydown', escuchar)
    return () => window.removeEventListener('keydown', escuchar)
  }, [])

  return (
    <div ref={raiz} className="sin-seleccion">
      {/* Los puntos muestran cuántos dígitos se cargaron sin revelar el PIN a
          quien esté mirando desde el otro lado del mostrador. */}
      <div className="mb-6 flex justify-center gap-3" aria-hidden>
        {Array.from({ length: largo }).map((_, i) => (
          <span
            key={i}
            className={clsx(
              'h-3.5 w-3.5 rounded-full border-2 transition-all',
              i < valor.length
                ? 'scale-110 border-verde-claro bg-verde-claro'
                : oscuro
                  ? 'border-crema/25 bg-transparent'
                  : 'border-borde-fuerte bg-transparent',
            )}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <Tecla key={d} onClick={() => agregar(d)} deshabilitado={deshabilitado} oscuro={oscuro}>
            {d}
          </Tecla>
        ))}

        <span />

        <Tecla onClick={() => agregar('0')} deshabilitado={deshabilitado} oscuro={oscuro}>
          0
        </Tecla>

        <Tecla
          onClick={borrar}
          deshabilitado={deshabilitado || valor.length === 0}
          oscuro={oscuro}
          aria-label="Borrar"
        >
          <Delete className="h-5 w-5" />
        </Tecla>
      </div>
    </div>
  )
}

function Tecla({
  children,
  onClick,
  deshabilitado,
  oscuro,
  ...resto
}: {
  children: React.ReactNode
  onClick: () => void
  deshabilitado?: boolean
  oscuro?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={deshabilitado}
      className={clsx(
        'num flex h-16 items-center justify-center rounded-lg border text-2xl font-semibold',
        'transition-all active:scale-[0.97] disabled:opacity-40 disabled:active:scale-100',
        oscuro
          ? 'border-crema/12 bg-crema/[0.06] text-crema hover:border-verde-claro/40 hover:bg-crema/[0.12]'
          : 'border-borde bg-papel text-tinta shadow-sutil hover:border-borde-fuerte hover:bg-crema-alt active:bg-crema-alt',
      )}
      {...resto}
    >
      {children}
    </button>
  )
}
